import { Navigate, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useUserStore } from "../../../store/user_store"
import classNames from "classnames";

// Tab icons as inline SVGs
const tabIcons = {
    profile: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    ),
    list: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
    ),
    favorites: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
        </svg>
    ),
    chats: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
    ),
    wallets: (
        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
        </svg>
    ),
};

const tabs = [
    { path: "/profile", label: "Профиль", shortLabel: "Профиль", icon: "profile" },
    { path: "/profile/list", label: "Объявления", shortLabel: "Мои", icon: "list" },
    { path: "/profile/favorites", label: "Избранные", shortLabel: "Избранные", icon: "favorites" },
    { path: "/profile/chats", label: "Сообщения", shortLabel: "Чаты", icon: "chats" },
    { path: "/profile/wallets", label: "Баланс", shortLabel: "Баланс", icon: "wallets" },
];

function Profilelayout({ children }) {
    const auth = useUserStore()
    const user = auth.user
    const navigate = useNavigate()
    const location = useLocation()

    const isActive = (path) => location.pathname === path;

    if (user === null || user === undefined || user === "") {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="mt-4 text-sm text-slate-400 font-medium">Загрузка...</p>
            </div>
        )
    }

    return (
        <div className="w-full mt-0 sm:mt-3 px-0">
            <div className="w-full px-0">
                <div className="w-full px-0 pb-20 sm:pb-3">
                    {/* Desktop Navigation Tabs — hidden on mobile */}
                    <nav className="hidden sm:block bg-white border border-slate-100 rounded-2xl p-1.5 shadow-sm mb-4">
                        <div className="flex gap-1 overflow-x-auto scrollbar-hide">
                            {tabs.map((tab) => (
                                <Link
                                    key={tab.path}
                                    to={tab.path}
                                    className={classNames(
                                        "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap min-w-0",
                                        {
                                            "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-500/20": isActive(tab.path),
                                            "text-slate-500 hover:text-slate-700 hover:bg-slate-50": !isActive(tab.path),
                                        }
                                    )}
                                >
                                    <span className={classNames(
                                        "flex-shrink-0 transition-colors",
                                        {
                                            "text-white": isActive(tab.path),
                                            "text-slate-400": !isActive(tab.path),
                                        }
                                    )}>
                                        {tabIcons[tab.icon]}
                                    </span>
                                    <span className="truncate">{tab.label}</span>
                                </Link>
                            ))}
                        </div>
                    </nav>

                    {/* Tab Content */}
                    <div className="sm:mt-0">
                        <Outlet />
                    </div>

                    {/* Mobile Bottom Navigation Bar — fixed at bottom, visible only on mobile */}
                    <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-[999] bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
                        <div className="flex items-stretch justify-around px-1 py-1.5 safe-area-pb">
                            {tabs.map((tab) => (
                                <Link
                                    key={tab.path}
                                    to={tab.path}
                                    className={classNames(
                                        "flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-xl min-w-0 flex-1 transition-all duration-200",
                                        {
                                            "text-blue-600": isActive(tab.path),
                                            "text-slate-400": !isActive(tab.path),
                                        }
                                    )}
                                >
                                    {/* Active indicator dot */}
                                    <div className="relative">
                                        <span className={classNames(
                                            "transition-colors duration-200",
                                            {
                                                "text-blue-600": isActive(tab.path),
                                                "text-slate-400": !isActive(tab.path),
                                            }
                                        )}>
                                            {tabIcons[tab.icon]}
                                        </span>
                                        {isActive(tab.path) && (
                                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full shadow-sm shadow-blue-500/50" />
                                        )}
                                    </div>
                                    <span className={classNames(
                                        "text-[10px] font-medium truncate max-w-full transition-colors duration-200",
                                        {
                                            "text-blue-600 font-semibold": isActive(tab.path),
                                            "text-slate-400": !isActive(tab.path),
                                        }
                                    )}>
                                        {tab.shortLabel}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </nav>
                </div>
            </div>
        </div>
    )
}

export default Profilelayout